import {
    AttachedFiles,
    BTFileSystem,
    type IHasAttachedFiles,
} from "legacyComponents/FileUploadContainer.types";

import { CostTypes, DocumentInstanceType } from "types/enum";

import { hasProposalBeenActedOn } from "utilities/proposals/proposal.utils";

import {
    type BTFileSystem as BTFileSystemApiV2,
    OptionMode,
    ProposalColumnTypes,
    type ProposalForDisplay,
    type ProposalForDisplayAttachedFilesBase,
    type ProposalForDisplayFormatItemsItem,
    type ProposalGroupForDisplay,
    type ProposalLineItemForDisplay,
    type ProposalOption,
} from "handlers";

import { UnassignedCostGroupId } from "entity/estimate/common/estimate.common.types";
import { ProposalBodyLayouts } from "entity/estimate/Proposal/Proposal.types";

export const formattedProposalFileThumbnailWidth = 160;
export const formattedProposalFileThumbnailHeight = 100;

export const getFormattedProposalCostTypeText = (costType: CostTypes) => {
    switch (costType) {
        case CostTypes.Allowance:
            return "Allowance";
        case CostTypes.Bid:
            return "Bid";
        case CostTypes.Equipment:
            return "Equipment";
        case CostTypes.Labor:
            return "Labor";
        case CostTypes.Material:
            return "Material";
        case CostTypes.Other:
            return "Other";
        case CostTypes.Subcontractor:
            return "Subcontractor";
        case CostTypes.None:
            return "None";
    }
};

export const isProposalGroupForDisplay = (
    formatItem: ProposalGroupForDisplay | ProposalLineItemForDisplay,
): formatItem is ProposalGroupForDisplay => {
    return (formatItem as ProposalGroupForDisplay).items !== undefined;
};

export const isProposalGrouped = (
    formatItems: readonly ProposalForDisplayFormatItemsItem[],
): formatItems is ProposalGroupForDisplay[] => {
    return formatItems.length > 0 && isProposalGroupForDisplay(formatItems[0]);
};

export const isProposalLineItemForDisplay = (
    formatItem: ProposalGroupForDisplay | ProposalLineItemForDisplay,
): formatItem is ProposalLineItemForDisplay => {
    return (formatItem as ProposalLineItemForDisplay).costCodeTitle !== undefined;
};

export const getAllGroupsFromProposalForDisplayData = (
    data: readonly ProposalForDisplayFormatItemsItem[],
): ProposalGroupForDisplay[] => {
    const rootGroups = data.filter(isProposalGroupForDisplay);
    return [
        ...rootGroups,
        ...rootGroups.flatMap((group) => getAllGroupsFromProposalForDisplayData(group.items)),
    ];
};

/**
 * Counts the spec instances that will actually mount for the given layout.
 *  - Column: strictly two levels. Top-level group specs render when that group is expanded; subgroup
 *    specs render in the (always-rendered) panel header, so they only require the parent group to be
 *    expanded. Deeper nesting is not rendered by this layout, so it is not counted.
 *  - MultiLine: arbitrarily nested. A group's specs render in its panel body, gated on that group's
 *    own `isExpanded`, and a group only renders at all when every ancestor is expanded — so a spec
 *    mounts if the group and all of its ancestors are expanded.
 */
export const getRenderedProposalSpecCount = (
    formatItems: readonly ProposalForDisplayFormatItemsItem[],
    whatToDisplay: readonly ProposalColumnTypes[],
    bodyLayout: ProposalBodyLayouts,
): number => {
    if (!whatToDisplay.includes(ProposalColumnTypes.Specs)) {
        return 0;
    }

    const specCount = (group: ProposalGroupForDisplay) => group.specifications?.length ?? 0;
    const topLevelGroups = formatItems.filter(isProposalGroupForDisplay);

    if (bodyLayout === ProposalBodyLayouts.None) {
        return 0;
    }

    if (bodyLayout === ProposalBodyLayouts.Column) {
        return topLevelGroups.reduce((total, group) => {
            if (!group.isExpanded) {
                return total;
            }
            // Parent expanded: count its own specs plus its direct subgroups' header specs.
            const subGroupSpecs = group.items
                .filter(isProposalGroupForDisplay)
                .reduce((subTotal, subGroup) => subTotal + specCount(subGroup), 0);
            return total + specCount(group) + subGroupSpecs;
        }, 0);
    }

    // MultiLine: a group's specs mount only when the group and every ancestor are expanded.
    const countExpandedDescendants = (group: ProposalGroupForDisplay): number => {
        if (!group.isExpanded) {
            return 0;
        }
        return group.items
            .filter(isProposalGroupForDisplay)
            .reduce(
                (total, subGroup) => total + countExpandedDescendants(subGroup),
                specCount(group),
            );
    };

    return topLevelGroups.reduce((total, group) => total + countExpandedDescendants(group), 0);
};

export const getAllLineItemsFromProposalForDisplayData = (
    data: readonly ProposalForDisplayFormatItemsItem[],
): ProposalLineItemForDisplay[] => {
    const rootGroups = data.filter(isProposalGroupForDisplay);
    return [
        ...data.filter(isProposalLineItemForDisplay),
        ...rootGroups.flatMap((group) => getAllLineItemsFromProposalForDisplayData(group.items)),
    ];
};

const getUpdatedProposalGroupForDisplayData = (
    group: ProposalGroupForDisplay,
    updateMap: Map<number, ProposalGroupForDisplay | ProposalLineItemForDisplay>,
): ProposalGroupForDisplay => {
    const updatedGroup = updateMap.get(group.proposalFormatItemId);

    if (updatedGroup && isProposalGroupForDisplay(updatedGroup)) {
        return updatedGroup;
    }

    return {
        ...group,
        items: group.items.map((item) => {
            if (isProposalGroupForDisplay(item)) {
                return getUpdatedProposalGroupForDisplayData(item, updateMap);
            }
            return updateMap.get(item.proposalFormatItemId) ?? item;
        }),
    };
};

export const getUpdatedProposalForDisplayData = (
    currentData: ProposalGroupForDisplay[],
    dataToUpdate: readonly ProposalForDisplayFormatItemsItem[],
): ProposalGroupForDisplay[] => {
    if (currentData.length === 0) {
        return currentData;
    }

    const updateMap: Map<number, ProposalGroupForDisplay | ProposalLineItemForDisplay> = new Map(
        dataToUpdate.map((row) => [row.proposalFormatItemId, row]),
    );
    return currentData.map((item) => {
        return getUpdatedProposalGroupForDisplayData(item, updateMap);
    });
};

export const getIHasAttachedFilesForProposal = (
    builderId: number,
    jobId: number,
    worksheetId: number,
    proposalId: number,
    attachedFilesBase: ProposalForDisplayAttachedFilesBase,
    proposalFiles: readonly BTFileSystemApiV2[],
): IHasAttachedFiles => {
    const isDraftProposal = proposalId === 0;
    return {
        builderId,
        jobId,
        id: isDraftProposal ? worksheetId : proposalId,
        documentInstanceType: isDraftProposal
            ? DocumentInstanceType.Estimate
            : DocumentInstanceType.ProposalSnapshot,
        name: "Job Proposal",
        attachedFiles: {
            ...AttachedFiles.fromProposalForDisplayAttachedFilesBaseApiV2(attachedFilesBase)!,
            files: proposalFiles.map((file) => BTFileSystem.fromBTFileSystemApiV2(file)),
        },
    };
};

export const getIHasAttachedFilesForProposalSpec = (
    builderId: number,
    jobId: number,
    proposalId: number,
    attachedFilesBase: ProposalForDisplayAttachedFilesBase,
    specFiles: readonly BTFileSystemApiV2[],
): IHasAttachedFiles => ({
    builderId,
    jobId,
    id: proposalId,
    documentInstanceType: DocumentInstanceType.ProposalSnapshot,
    name: "Specification",
    attachedFiles: {
        ...AttachedFiles.fromProposalForDisplayAttachedFilesBaseApiV2(attachedFilesBase)!,
        files: specFiles.map((file) => BTFileSystem.fromBTFileSystemApiV2(file)),
    },
});

export const getIHasAttachedFilesForProposalGroup = (
    builderId: number,
    jobId: number,
    proposalId: number,
    proposalFormatItemId: number,
    attachedFilesBase: ProposalForDisplayAttachedFilesBase,
    groupFiles: readonly BTFileSystemApiV2[],
): IHasAttachedFiles => {
    const isDraftProposal = proposalId === 0;
    return {
        builderId,
        jobId,
        id: isDraftProposal ? proposalFormatItemId : proposalId,
        documentInstanceType: isDraftProposal
            ? DocumentInstanceType.ProposalFormatItem
            : DocumentInstanceType.ProposalSnapshot,
        name: "Proposal Group",
        attachedFiles: {
            ...AttachedFiles.fromProposalForDisplayAttachedFilesBaseApiV2(attachedFilesBase)!,
            files: groupFiles.map((file) => BTFileSystem.fromBTFileSystemApiV2(file)),
        },
    };
};

export const doesProposalHaveTaxableLineItems = (
    formatItems: ProposalGroupForDisplay[] | ProposalLineItemForDisplay[],
): boolean => {
    const lineItems = getAllLineItemsFromProposalForDisplayData(formatItems);
    return lineItems.some((lineItem) => lineItem.taxGroupId != null);
};

export const getProposalOptions = (proposalData: ProposalForDisplay): ProposalOption[] => {
    if (isProposalGrouped(proposalData.formatItems) && !hasProposalBeenActedOn(proposalData)) {
        const options: ProposalOption[] = proposalData.formatItems
            .filter(
                (group) =>
                    group.proposalFormatItemId !== UnassignedCostGroupId &&
                    group.optionMode !== OptionMode.Required,
            )
            .map((group) => {
                return {
                    id: group.proposalFormatItemId,
                    optionMode: group.optionMode,
                };
            });
        return options;
    } else {
        return [];
    }
};
